import React, { useEffect, useState, useMemo } from "react";
import { toast } from "react-toastify";
import { FaEdit, FaTrash } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import {
  getQuestionGames,
  deleteQuestionGame,
} from "../../services/questionGameService";
import { getMatches } from "../../services/matchService";
import { getLeagues } from "../../services/leagueService";

export const QuestiongameListLayer = () => {
  const navigate = useNavigate();

  const [games, setGames] = useState([]);
  const [matches, setMatches] = useState([]);
  const [leagues, setLeagues] = useState([]);

  const [filterLeague, setFilterLeague] = useState("");
  const [filterMatch, setFilterMatch] = useState("");
  const [search, setSearch] = useState("");

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    const [gamesRes, matchRes, leagueRes] =
      await Promise.all([
        getQuestionGames(),
        getMatches(),
        getLeagues(),
      ]);

    if (gamesRes?.status) setGames(gamesRes.data || []);
    if (matchRes?.status) setMatches(matchRes.data || []);
    if (leagueRes?.status) setLeagues(leagueRes.data || []);
  };

  const leagueMatches = useMemo(() => {
    if (!filterLeague) return matches;
    return matches.filter(
      (m) => String(m.league_id) === String(filterLeague)
    );
  }, [filterLeague, matches]);

  const filtered = useMemo(() => {
    return games.filter((g) => {
      const match = matches.find(
        (m) => String(m.id) === String(g.match_id)
      );

      return (
        (!filterLeague ||
          String(match?.league_id) === String(filterLeague)) &&
        (!filterMatch ||
          String(g.match_id) === String(filterMatch)) &&
        (!search ||
          g.game_title
            .toLowerCase()
            .includes(search.toLowerCase()))
      );
    });
  }, [games, matches, filterLeague, filterMatch, search]);

  const totalPages = Math.ceil(filtered.length / pageSize);
  const data = filtered.slice(
    (page - 1) * pageSize,
    page * pageSize
  );

  const getMatchName = (id) => {
    const match = matches.find(
      (m) => String(m.id) === String(id)
    );
    return match
      ? `${match.team_a} vs ${match.team_b}`
      : "-";
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this game?")) return;
    const res = await deleteQuestionGame(id);
    if (res?.status) {
      toast.success("Deleted Successfully");
      loadData();
    }
  };

  return (
    <div className="container-fluid mt-4">
      <div className="card shadow-sm">

        <div className="card-header bg-white d-flex justify-content-between">
          <h5 className="fw-bold mb-0">
            Question Games
          </h5>
          <button
            className="btn btn-primary btn-sm"
            onClick={() =>
              navigate("/add-questiongame")
            }
          >
            + Add Game
          </button>
        </div>

        <div className="card-body">

          {/* FILTER BAR */}
          <div className="row g-2 mb-3">
            <div className="col-md-3">
              <select
                className="form-control"
                value={filterLeague}
                onChange={(e) => {
                  setFilterLeague(e.target.value);
                  setFilterMatch("");
                  setPage(1);
                }}
              >
                <option value="">All Leagues</option>
                {leagues.map((l) => (
                  <option key={l.id} value={l.id}>
                    {l.league_name}
                  </option>
                ))}
              </select>
            </div>

            <div className="col-md-3">
              <select
                className="form-control"
                value={filterMatch}
                onChange={(e) => {
                  setFilterMatch(e.target.value);
                  setPage(1);
                }}
              >
                <option value="">All Matches</option>
                {leagueMatches.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.team_a} vs {m.team_b}
                  </option>
                ))}
              </select>
            </div>

            <div className="col-md-3">
              <input
                className="form-control"
                placeholder="Search..."
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(1);
                }}
              />
            </div>

            <div className="col-md-3">
              <select
                className="form-control"
                value={pageSize}
                onChange={(e) => {
                  setPageSize(Number(e.target.value));
                  setPage(1);
                }}
              >
                <option value={10}>10</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
            </div>
          </div>

          {/* TABLE */}
          <div className="table-responsive">
            <table className="table table-bordered table-hover align-middle">
              <thead className="table-light">
                <tr>
                  <th>#</th>
                  <th>Match</th>
                  <th>Title</th>
                  <th>Points</th>
                  <th>Admin %</th>
                  <th>Winner %</th>
                  <th>Status</th>
                  <th>Options</th>
                  <th>Action</th>
                </tr>
              </thead>

              <tbody>
                {data.length === 0 ? (
                  <tr>
                    <td colSpan="9" className="text-center">
                      No records found
                    </td>
                  </tr>
                ) : (
                  data.map((g, i) => (
                    <tr key={g.id}>
                      <td>
                        {(page - 1) * pageSize + i + 1}
                      </td>
                      <td>{getMatchName(g.match_id)}</td>
                      <td>{g.game_title}</td>
                      <td>{g.points_rate}</td>
                      <td>{g.admin_percent}%</td>
                      <td>{g.winner_percentage}%</td>
                      <td>{g.status}</td>
                      <td>{g.ans_options?.length || 0}</td>
                      <td>
                        <button
                          className="btn btn-sm btn-outline-primary me-2"
                          onClick={() =>
                            navigate(
                              `/add-questiongame/${g.id}`
                            )
                          }
                        >
                          <FaEdit />
                        </button>
                        <button
                          className="btn btn-sm btn-outline-danger"
                          onClick={() =>
                            handleDelete(g.id)
                          }
                        >
                          <FaTrash />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* PAGINATION */}
          {totalPages > 1 && (
            <div className="d-flex justify-content-end mt-3">
              <ul className="pagination mb-0">
                <li className={`page-item ${page === 1 ? "disabled" : ""}`}>
                  <button className="page-link" onClick={() => setPage(page - 1)}>
                    Prev
                  </button>
                </li>

                {[...Array(totalPages)].map((_, i) => (
                  <li
                    key={i}
                    className={`page-item ${page === i + 1 ? "active" : ""}`}
                  >
                    <button
                      className="page-link"
                      onClick={() => setPage(i + 1)}
                    >
                      {i + 1}
                    </button>
                  </li>
                ))}

                <li
                  className={`page-item ${
                    page === totalPages ? "disabled" : ""
                  }`}
                >
                  <button
                    className="page-link"
                    onClick={() => setPage(page + 1)}
                  >
                    Next
                  </button>
                </li>
              </ul>
            </div>
          )}

        </div>
      </div>
    </div>
  );
};